// 8-bit pixel sprites for PCKT and the enemies. Each sprite is a grid of
// characters; '.' / ' ' = transparent, every other char maps to a hex color via
// the sprite's legend. Rendered as crisp scaled blocks (nearest-neighbor look)
// with a soft neon glow behind, so characters read as pixel art while keeping
// the 80s-arcade bloom.

export interface Sprite {
  w: number;
  h: number;
  rows: string[];
  legend: Record<string, string>;
}

function s(rows: string[], legend: Record<string, string>): Sprite {
  return { w: rows[0].length, h: rows.length, rows, legend };
}

// PCKT — digital packet lifeform: cyan hex body, glowing white core, antennae.
const pckt = s(
  [
    "...o...o...",
    "...o...o...",
    "....ccc....",
    "...ccccc...",
    "..ccwwwcc..",
    ".ccwwwwwcc.",
    "..ccwwwcc..",
    "...ccccc...",
    "....ccc....",
    "...........",
    "...........",
  ],
  { o: "#3a5fb0", c: "#80a0ff", w: "#e4e4e4" },
);

// Virus — spiky red icosahedron with a pale core.
const virus = s(
  [
    "v...v...v",
    ".v..v..v.",
    "..vvvvv..",
    ".vvvvvvv.",
    "vvvvwvvvv",
    ".vvvvvvv.",
    "..vvvvv..",
    ".v..v..v.",
    "v...v...v",
  ],
  { v: "#ff5454", w: "#7a1f1f" },
);

// Worm — fast little segmented streak with a bright head.
const worm = s(
  [
    ".........",
    ".hh......",
    "hhoo.....",
    ".oooo....",
    "..oooo...",
    "...oooo..",
    "....oooo.",
    "......oo.",
    ".........",
  ],
  { o: "#e3c78a", h: "#ff5454" },
);

// Glitch — corrupted green square with missing/extra pixels.
const glitch = s(
  [
    ".gg..gg..",
    "gggggggg.",
    "gg.gggg.g",
    "ggggggg.g",
    ".gggggggg",
    "gg.gggg.g",
    ".gggggg..",
    "g..gg..g.",
    ".........",
  ],
  { g: "#36c692" },
);

// Packet Thief — purple sprite hugging a yellow loot sack.
const thief = s(
  [
    "...pp....",
    "..pppp...",
    ".pppppp..",
    "pp.pp.pp.",
    "pppppppp.",
    ".pyyyyp..",
    ".pyyyyp..",
    ".pppppp..",
    ".........",
  ],
  { p: "#ae81ff", y: "#c6c684" },
);

// Noise — gray static cloud of scattered pixels.
const noise = s(
  [
    ".n.n.n.n.",
    "n.nnnn.n.",
    ".nnnnnn..",
    "n.nnnn.n.",
    ".nnnnnnn.",
    "n.nnnn.n.",
    ".n.nn.n..",
    "n..n.n..n",
    ".n.n.n.n.",
  ],
  { n: "#949494" },
);

// Corruptor — magenta radiator with a bright core (buffs nearby enemies).
const corruptor = s(
  [
    "....m....",
    "..m.m.m..",
    "...mmm...",
    ".mmmmmmm.",
    "m.mmwmm.m",
    ".mmmmmmm.",
    "...mmm...",
    "..m.m.m..",
    "....m....",
  ],
  { m: "#ff5189", w: "#4a0a1f" },
);

export const SPRITES: Record<string, Sprite> = {
  pckt,
  virus,
  worm,
  glitch,
  thief,
  noise,
  corruptor,
};

/** Map an enemy id (falling back to behavior) to a sprite. */
export function enemySprite(id: string, behavior: string): Sprite {
  return SPRITES[id] ?? SPRITES[behavior] ?? virus;
}

/**
 * Draw a sprite centered at (cx, cy). `pixel` is the size of one sprite cell in
 * screen pixels (integer for crisp edges). `flash` renders it solid white.
 */
export function drawSprite(
  ctx: CanvasRenderingContext2D,
  sprite: Sprite,
  cx: number,
  cy: number,
  pixel: number,
  flash = false,
): void {
  const ox = Math.round(cx - (sprite.w * pixel) / 2);
  const oy = Math.round(cy - (sprite.h * pixel) / 2);
  for (let r = 0; r < sprite.h; r++) {
    const row = sprite.rows[r];
    for (let c = 0; c < sprite.w; c++) {
      const ch = row[c];
      if (ch === "." || ch === " ") continue;
      ctx.fillStyle = flash ? "#e4e4e4" : sprite.legend[ch] ?? "#e4e4e4";
      ctx.fillRect(ox + c * pixel, oy + r * pixel, pixel, pixel);
    }
  }
}

/** Integer pixel size so a sprite roughly fills a given on-screen radius. */
export function pixelFor(radius: number, spriteH: number, min = 2): number {
  return Math.max(min, Math.round((radius * 2.2) / spriteH));
}
